"use client";

import * as Headless from "@headlessui/react";
import clsx from "clsx";
import { LayoutGroup, motion } from "framer-motion";
import React, { forwardRef, useId } from "react";
import { TouchTarget } from "./button";
import { Link } from "./link";

export function Sidebar({ className, ...props }: React.ComponentPropsWithoutRef<"nav">) {
  return <nav {...props} className={clsx(className, "flex h-full min-h-0 flex-col bg-white dark:bg-avemio-dark-surface border-r border-gray-200 dark:border-avemio-dark-border")} />;
}

export function SidebarHeader({ className, ...props }: React.ComponentPropsWithoutRef<"div">) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        "flex flex-col border-b border-gray-200 px-3 py-4 dark:border-avemio-dark-border [&>[data-slot=section]+[data-slot=section]]:mt-2",
      )}
    />
  );
}

export function SidebarBody({ className, ...props }: React.ComponentPropsWithoutRef<"div">) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        "flex flex-1 flex-col overflow-y-auto px-3 py-4 [&>[data-slot=section]+[data-slot=section]]:mt-6",
      )}
    />
  );
}

export function SidebarFooter({ className, ...props }: React.ComponentPropsWithoutRef<"div">) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        "flex flex-col border-t border-gray-200 px-3 py-3 dark:border-avemio-dark-border [&>[data-slot=section]+[data-slot=section]]:mt-2",
      )}
    />
  );
}

export function SidebarSection({ className, ...props }: React.ComponentPropsWithoutRef<"div">) {
  const id = useId();

  return (
    <LayoutGroup id={id}>
      <div {...props} data-slot="section" className={clsx(className, "flex flex-col gap-1")} />
    </LayoutGroup>
  );
}

export function SidebarDivider({ className, ...props }: React.ComponentPropsWithoutRef<"hr">) {
  return <hr {...props} className={clsx(className, "my-4 border-t border-gray-200 lg:-mx-4 dark:border-avemio-dark-border")} />;
}

export function SidebarSpacer({ className, ...props }: React.ComponentPropsWithoutRef<"div">) {
  return <div aria-hidden="true" {...props} className={clsx(className, "mt-8 flex-1")} />;
}

export function SidebarHeading({ className, ...props }: React.ComponentPropsWithoutRef<"h3">) {
  return (
    <h3 {...props} className={clsx(className, "mb-1.5 px-3 text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-avemio-dark-text-secondary")} />
  );
}

export const SidebarItem = forwardRef(function SidebarItem(
  {
    current,
    className,
    children,
    ...props
  }: { current?: boolean; className?: string; children: React.ReactNode } & (
    | Omit<Headless.ButtonProps, "as" | "className">
    | Omit<Headless.ButtonProps<typeof Link>, "as" | "className">
  ),
  ref: React.ForwardedRef<HTMLAnchorElement | HTMLButtonElement>,
) {
  const classes = clsx(
    // Base - enterprise spacing and sizing
    "relative flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-sm font-medium text-gray-900",
    // Subtle left border for hover effect
    "border-l-3 border-l-transparent",
    // Beautiful smooth transitions - multiple properties
    "transition-all duration-200 ease-out",
    // Leading icon/icon-only
    "*:data-[slot=icon]:size-4 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:fill-gray-600 *:data-[slot=icon]:transition-all *:data-[slot=icon]:duration-200",
    // Trailing icon (down chevron or similar) 
    "*:last:data-[slot=icon]:ml-auto *:last:data-[slot=icon]:size-4",
    // Avatar
    "*:data-[slot=avatar]:-m-0.5 *:data-[slot=avatar]:size-8 *:data-[slot=avatar]:[--ring-opacity:10%] *:data-[slot=avatar]:transition-transform *:data-[slot=avatar]:duration-200",
    // Beautiful Hover - multiple coordinated changes
    "data-hover:bg-gray-50/80 data-hover:border-l-avemio-primary/40 data-hover:translate-x-0.5",
    "data-hover:*:data-[slot=icon]:fill-gray-900 data-hover:*:data-[slot=icon]:scale-105",
    "data-hover:shadow-sm",
    // Active state
    "data-active:bg-gray-100 data-active:*:data-[slot=icon]:fill-gray-900",
    // Current page - pronounced but professional
    "data-current:bg-avemio-primary/8 data-current:text-avemio-primary data-current:border-l-avemio-primary",
    "data-current:*:data-[slot=icon]:fill-avemio-primary data-current:font-semibold",
    // Dark mode - beautiful professional hover
    "dark:text-avemio-dark-text-primary dark:*:data-[slot=icon]:fill-avemio-dark-text-secondary",
    "dark:data-hover:bg-avemio-dark-surface-elevated/80 dark:data-hover:border-l-avemio-primary/50",
    "dark:data-hover:*:data-[slot=icon]:fill-avemio-dark-text-primary dark:data-hover:shadow-soft",
    "dark:data-active:bg-avemio-dark-surface-elevated",
    "dark:data-current:bg-avemio-primary/12 dark:data-current:text-avemio-primary dark:data-current:border-l-avemio-primary",
    "dark:data-current:*:data-[slot=icon]:fill-avemio-primary",
  );

  return (
    <span className={clsx(className, "relative block")}>
      {current && (
        <motion.span
          layoutId="current-indicator"
          className="absolute inset-y-2 -left-3 w-1 rounded-r-full bg-avemio-primary dark:bg-avemio-primary shadow-sm pointer-events-none"
          initial={{ opacity: 0, x: -4 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        />
      )}
      {"href" in props && typeof props.href === "string" ? (
        (() => {
          const { to, ...linkProps } = props as any;
          return (
            <Link
              {...linkProps}
              href={props.href}
              className={classes}
              data-current={current ? "true" : undefined}
              ref={ref as React.ForwardedRef<HTMLAnchorElement>}
              onClick={(e) => {
                console.log('[SidebarItem] Clicked, navigating to:', props.href);
              }}
            >
              {children}
            </Link>
          );
        })()
      ) : (
        (() => {
          const { to, href, download, hrefLang, ...buttonProps } = props as any;
          return (
            <Headless.Button
              {...buttonProps}
              className={clsx("cursor-pointer", classes)}
              data-current={current ? "true" : undefined}
              ref={ref}
            >
              {children}
            </Headless.Button>
          );
        })()
      )}
    </span>
  );
});

export function SidebarLabel({ className, ...props }: React.ComponentPropsWithoutRef<"span">) {
  return <span {...props} className={clsx(className, "truncate")} />;
}
